import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ILink } from '@ncg/data';
import stringify from 'fast-safe-stringify';
import { firstValueFrom, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AutoProffService } from '../../../auto-proff/auto-proff.service';
import { ScriptLoadService } from '../../../core/script-load.service';
import { SettingsService } from '../../../core/settings.service';
import { TrackingService } from '../../../core/tracking.service';
import { SidePanelService } from '../../../side-panel/side-panel.service';
import { DialogService } from '../../../utils/dialog/dialog.service';
import { IAuEmbed, IAuws, IDixaInitial, IDixaLoaded, IWeply } from './action-bar.models';

@Injectable()
export class ActionBarService implements OnDestroy {
    private readonly unsubscribe = new Subject<void>();

    private readonly solgtComBaseUrl: string = 'https://solgt.com/widgets/form-widget.js';
    private readonly autoUncleBaseUrl: string = 'https://www.autouncle.dk/da/customers';
    private readonly weplyBaseUrl: string = 'https://app.weply.chat/widget';
    private readonly dixaBaseUrl: string = 'https://messenger.dixa.io/bootstrap.js';

    private dixaLoaded: boolean = false;
    private dixaOpen: boolean = false;
    private weplyLoaded: boolean = false;
    private autoUncleLoaded: boolean = false;
    private solgtComLoaded: boolean = false;

    constructor(
        private readonly router: Router,
        private readonly sidePanelService: SidePanelService,
        private readonly dialogService: DialogService,
        private readonly scriptLoadService: ScriptLoadService,
        private readonly trackingService: TrackingService,
        private readonly autoProffService: AutoProffService,
        private readonly settingsService: SettingsService
    ) {}

    public ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public openInSidePanel(link?: ILink) {
        if (link?.url) {
            this.sidePanelService.openContent(link.url);
        }
    }

    public openLink(link?: ILink) {
        if (link) {
            if (link.target) {
                window.open(link.url, link.target);
            } else if (!link.url.includes('http')) {
                this.router.navigateByUrl(link.url).catch(console.error);
            } else {
                location.href = link.url;
            }
        }
    }

    public async openDixa(token?: string) {
        if (token && (await this.checkConsent())) {
            this.dixaLoaded = this.dixaLoaded || Boolean(window.dixa) || this.scriptLoadService.isScriptInserted(this.dixaBaseUrl);
            if (!this.dixaLoaded) {
                const language = await firstValueFrom(this.settingsService.get().pipe(map((s) => s.seoLanguage)));
                this.loadDixa(token, language);
            } else if (window._dixa_) {
                window._dixa_.invoke('setWidgetOpen', !this.dixaOpen);
            }
        }
    }

    public async openWeply(id?: string) {
        if (id) {
            if (await this.checkConsent()) {
                this.weplyLoaded = this.weplyLoaded || Boolean(window.$$weply) || this.scriptLoadService.isScriptInserted(this.weplyBaseUrl);
                if (!this.weplyLoaded) {
                    this.loadWeply(id);
                } else if (window.$$weply) {
                    window.$$weply.api.publicApi.widget.maximize();
                }
            }
        }
    }

    public async openSolgtCom(snippet?: string) {
        if (snippet) {
            if (await this.checkConsent()) {
                this.solgtComLoaded = this.solgtComLoaded || Boolean(window.solgt) || this.scriptLoadService.isScriptInserted(this.solgtComBaseUrl);
                if (!this.solgtComLoaded) {
                    this.dialogService
                        .openDialog({
                            data: {
                                header: undefined,
                                text: snippet,
                                textIsSafe: true,
                                dialogClassModifier: 'dialog--action-bar',
                            },
                        })
                        .then((overlayRef) => {
                            this.loadSolgtCom();
                            overlayRef
                                .afterClose()
                                .pipe(takeUntil(this.unsubscribe))
                                .subscribe(() => this.unloadSolgtCom());
                        })
                        .catch(console.error);
                }
            }
        }
    }

    public async openAutoUncle(id?: string) {
        if (id) {
            if (await this.checkConsent()) {
                this.autoUncleLoaded = this.autoUncleLoaded || Boolean(window.auws) || this.scriptLoadService.isScriptInserted(this.autoUncleBaseUrl);
                if (window.auws) {
                    window.auws.openModal(window.auws.ModuleTypes.TRADE_IN);
                } else if (!this.autoUncleLoaded) {
                    this.loadAutoUncle(id, () => {
                        if (window.auws) {
                            window.auws.openModal(window.auws.ModuleTypes.TRADE_IN);
                        }
                    });
                }
            }
        }
    }

    public async openAutoProff(): Promise<boolean | void> {
        if (!this.autoProffService.openPopup()) {
            const isLoaded = await this.autoProffService.init();
            if (isLoaded) {
                this.autoProffService.openPopup();
            }
        }
        return Promise.resolve();
    }

    private loadDixa(token: string, language: string) {
        this.dixaLoaded = true;

        window.dixa = {
            invoke: (method, args) => {
                window._dixa_ = window._dixa_ ?? ({} as IDixaLoaded);
                window._dixa_.pendingCalls = window._dixa_.pendingCalls || [];
                window._dixa_.pendingCalls.push([method, args]);
            },

            addListener: (event, listener) => {
                window._dixa_ = window._dixa_ ?? ({} as IDixaLoaded);
                window._dixa_.pendingAddListenerCalls = window._dixa_.pendingAddListenerCalls || [];
                window._dixa_.pendingAddListenerCalls.push([event, listener]);
            },
        };
        window.dixa.addListener('on-widget-open-changed', this.onDixaOpenChanged);
        window.dixa.invoke('setWidgetOpen', true);
        window.dixa.invoke('init', { messengerToken: token, language, position: 'bottomLeft', hideToggler: true, showWidgetCloseButton: true });

        this.scriptLoadService.loadScript(this.dixaBaseUrl, {
            type: 'text/javascript',
            async: true,
            onerror: this.unloadDixa,
        });
    }

    private readonly onDixaOpenChanged = (isOpen: boolean) => {
        this.dixaOpen = isOpen;
    };

    private unloadDixa() {
        if (window._dixa_?.removeListener) {
            window._dixa_.removeListener('on-widget-open-changed', this.onDixaOpenChanged);
        }
        this.dixaLoaded = false;
        this.scriptLoadService.removeScript(this.dixaBaseUrl);
        window.dixa = undefined;
    }

    private loadWeply(id: string) {
        this.weplyLoaded = true;
        this.scriptLoadService.loadScript(`${this.weplyBaseUrl}/${id}`, {
            async: true,
            onerror: this.unloadWeply,
            callback: () => {
                setTimeout(() => {
                    window?.$$weply?.api.publicApi.widget.maximize();
                }, 1000);
            },
        });
    }

    private unloadWeply() {
        this.weplyLoaded = false;
        this.scriptLoadService.removeScript(this.weplyBaseUrl);
        window.$$weply = undefined;
    }

    private loadSolgtCom() {
        this.solgtComLoaded = true;
        this.scriptLoadService.loadScript(this.solgtComBaseUrl, {
            async: true,
            onerror: this.unloadSolgtCom,
        });
    }

    private unloadSolgtCom() {
        this.solgtComLoaded = false;
        this.scriptLoadService.removeScript(this.solgtComBaseUrl);
        window.solgt = undefined;
    }

    private loadAutoUncle(autoUncleId: string | undefined, loadCallback: () => void) {
        if (autoUncleId) {
            this.autoUncleLoaded = this.autoUncleLoaded || this.scriptLoadService.isScriptInserted(this.autoUncleBaseUrl);
            if (!this.autoUncleLoaded) {
                this.autoUncleLoaded = true;
                window.auws?.unload();
                if (!window.AuEmbed) {
                    window.AuEmbed = {
                        pushedUrls: [],
                        pushUrl: (url) => {
                            window.AuEmbed?.pushedUrls.push(url);
                        },
                    };
                }

                window.AuEmbed.config = {
                    id: autoUncleId,
                    onCustomerDetailsSent: () => {
                        this.trackingService.trackAutoUncle('tradeincar_complete');
                    },
                    onValuationDone: () => {
                        this.trackingService.trackAutoUncle('tradeincar_valuated');
                    },
                    onValuationFailed: () => {
                        this.trackingService.trackAutoUncle('tradeincar_failed');
                    },
                    onCarDetailsStarted: () => {
                        this.trackingService.trackAutoUncle('registration_number_type_in');
                    },
                };

                this.scriptLoadService.loadScript(
                    `${this.autoUncleBaseUrl}/${encodeURIComponent(window.AuEmbed.config.id)}/website_script.js?d=${encodeURIComponent(
                        stringify({
                            depId: window.AuEmbed.config.departmentId,
                            device: window.matchMedia('only screen and (max-width: 760px)').matches ? 'mobile' : 'desktop',
                            pageType: window.AuEmbed.config.pageType,
                            url: window.AuEmbed.config.previewUrl || document.location.href,
                        })
                    )}`,
                    {
                        id: 'au-cweb-script',
                        async: true,
                        onerror: this.unloadAutoUncle,
                        callback: loadCallback,
                    }
                );
            }
        }
    }

    private unloadAutoUncle() {
        this.autoUncleLoaded = false;
        window.auws?.unload();
        this.scriptLoadService.removeScript(this.autoUncleBaseUrl);
        window.auws = undefined;
    }

    private async checkConsent() {
        const consentLoaded = await firstValueFrom(this.trackingService.isCookieInformationLoaded$);
        const hasConsent = await firstValueFrom(this.trackingService.functionalConsentGiven());
        if (!consentLoaded) {
            this.dialogService
                .openDialog({
                    data: {
                        header: 'action_bar.ad_blocker_heading',
                        text: 'action_bar.ad_blocker_text',
                        textIsSafe: false,
                        disableOkBtn: true,
                    },
                })
                .catch(console.error);
            return false;
        }
        if (!hasConsent) {
            this.dialogService
                .openDialog({
                    data: {
                        header: 'action_bar.consent_dialog_heading',
                        text: 'action_bar.consent_dialog_text',
                        textIsSafe: false,
                        disableCloseBtn: true,
                    },
                })
                .then((overlayRef) => {
                    overlayRef
                        .afterClose()
                        .pipe(takeUntil(this.unsubscribe))
                        .subscribe((choice) => {
                            if (choice) {
                                this.trackingService.showCookieBanner();
                            }
                        });
                })
                .catch(console.error);
            return false;
        } else {
            return true;
        }
    }
}

declare global {
    interface Window {
        $$weply?: IWeply;
        auws?: IAuws;
        AuEmbed?: IAuEmbed;
        dixa?: IDixaInitial;
        _dixa_?: IDixaLoaded;
        solgt?: unknown;
    }
}
